import { GridCodeType } from "../../../../models";
import {
  affectsRisk,
  getSOIROMTooltip,
  getUsualFlagTooltip,
  isSOIROMFlag,
} from "../../utils/flags";

import type { BaseCodeRow } from "../../../../models/codeGrid";

// prefixes to identify tooltip column
export const TOOLTIP_CODECELL = "##_CodeCell_##_";
export const TOOLTIP_HCCCELL = "##_HCCCell_##_";
export const TOOLTIP_HCCCELL24 = "##_HCCCell24_##_";
export const TOOLTIP_HCCCELL28 = "##_HCCCell28_##_";
export const TOOLTIP_GFCELL = "##_GFCell_##_";
export const TOOLTIP_TEXTCELL = "##_TextCell_##_";
export const TOOLTIP_SICELL = "##_SICell_##_";
export const TOOLTIP_ERRORCELL = "##ErrorCell_##_";
export const TOOLTIP_WARNINGCELL = "##WarningCell_##_";

export enum TOOLTIP_ICON_TYPE {
  ERROR = "Error",
  NOICON = "No Icon",
  WARNING = "Warning",
}

export const combineTooltip = (type: string, fieldOrText: string, id?: string): string =>
  id ? `${type}${fieldOrText}##${id}` : `${type}${fieldOrText}`;

export const getTooltipType = (title?: string): string => {
  if (!title) return "";

  const tooltipTypes = [
    TOOLTIP_CODECELL,
    TOOLTIP_HCCCELL,
    TOOLTIP_HCCCELL24,
    TOOLTIP_HCCCELL28,
    TOOLTIP_GFCELL,
    TOOLTIP_TEXTCELL,
    TOOLTIP_SICELL,
    TOOLTIP_ERRORCELL,
    TOOLTIP_WARNINGCELL,
  ];

  return tooltipTypes.find((type) => title.startsWith(type)) || "";
};

export const getTooltipItemInfo = (title?: string): string => {
  if (!title) return "";

  const type = getTooltipType(title);
  return type ? title.substring(type.length) : "";
};

export const getTooltipItem = (id: string, gridData: BaseCodeRow[]): BaseCodeRow | null =>
  gridData.find((item) => item.id === id) || null;

export const needShowTooltipForCodeCell = (dataItem: any, fieldName = "Code"): boolean => {
  if (!dataItem?.Validations) return false;

  return dataItem.Validations.some((validation) => validation.Field === fieldName);
};

export const isCriticalLevelError = (errorLevel?: string | null): boolean =>
  errorLevel === "Critical" || errorLevel === "Medium" || errorLevel === "Low";

export const getTooltipIconTypeForCodeCell = (
  dataItem: any,
  fieldName = "Code",
): TOOLTIP_ICON_TYPE => {
  if (!dataItem?.Validations) return TOOLTIP_ICON_TYPE.NOICON;

  const validation = dataItem.Validations.find((v) => v.Field === fieldName);
  if (!validation) return TOOLTIP_ICON_TYPE.NOICON;

  return isCriticalLevelError(validation.Level) ?
      TOOLTIP_ICON_TYPE.ERROR
    : TOOLTIP_ICON_TYPE.WARNING;
};

export const getTooltipContentForCodeGridCell = (gridData: BaseCodeRow[], tooltip?: string) => {
  if (!tooltip) return null;

  const type = getTooltipType(tooltip);
  const info = getTooltipItemInfo(tooltip);
  if (!type || !info) return tooltip;

  // use raw text here
  if (type === TOOLTIP_TEXTCELL || type === TOOLTIP_ERRORCELL || type === TOOLTIP_WARNINGCELL) {
    return info;
  }

  // use multiline text
  if (type === TOOLTIP_SICELL) {
    const parts = info.split(String.fromCharCode(10));
    return (
      <span>
        {parts.map((part, index) => (
          <div key={index}>{part}</div>
        ))}
      </span>
    );
  }

  const pos = info.indexOf("##");
  if (pos <= 0 || pos === info.length - 1) return null;

  const field = info.substring(0, pos);
  const id = info.substring(pos + 2);
  const dataItem = getTooltipItem(id, gridData);
  if (!dataItem) return null;

  switch (type) {
    case TOOLTIP_CODECELL:
      return getTooltipContentCodeCell(dataItem, field);
    case TOOLTIP_HCCCELL:
      return getTooltipContentHCCCell(dataItem, "");
    case TOOLTIP_HCCCELL24:
      return getTooltipContentHCCCell(dataItem, "24");
    case TOOLTIP_HCCCELL28:
      return getTooltipContentHCCCell(dataItem, "28");
    case TOOLTIP_GFCELL:
      return getTooltipContentGFCell(dataItem, gridData[0].id === id);
    default:
      return null;
  }
};

export const getTooltipContentCodeCell = (dataItem: any, fieldName = "Code") => {
  if (!dataItem?.Validations) return null;

  const editsContent: JSX.Element[] = [];
  const warningEditsContent: JSX.Element[] = [];
  let hasCritical = false;

  dataItem.Validations.forEach((edit: any, index: number) => {
    if (!edit?.Description || edit.Field !== fieldName) return;

    if (isCriticalLevelError(edit.Level)) {
      hasCritical = true;
      editsContent.push(
        <span
          style={{ display: "block" }}
          key={index}
          dangerouslySetInnerHTML={{ __html: edit.Description }}
        />,
      );
    } else if (!hasCritical) {
      warningEditsContent.push(
        <span
          style={{ display: "block" }}
          key={index}
          dangerouslySetInnerHTML={{ __html: (index > 0 ? "<br/>" : "") + edit.Description }}
        />,
      );
    }
  });

  return (
    hasCritical ? editsContent
    : warningEditsContent.length > 0 ? warningEditsContent
    : null
  );
};

const getTooltipContentHCCCell = (dataItem: any, version: string) => {
  let { hccs } = dataItem;
  switch (version) {
    case "24":
      hccs = dataItem.hccs24;
      break;
    case "28":
      hccs = dataItem.hccs28;
      break;
    default:
      // Use default hccs value
      break;
  }

  if (!hccs?.length) {
    return (
      <span>
        <i>Hierarchical Condition Category (HCC)</i>
      </span>
    );
  }

  return (
    <>
      <span key="title">
        <i>Hierarchical Condition Category (HCC)</i>
      </span>
      {hccs.map(
        (hccItem: any, index: number) =>
          hccItem.Value &&
          hccItem.Description && (
            <span style={{ display: "block" }} key={index}>
              {hccItem.Value.toString()} - {hccItem.Description.toString()}
            </span>
          ),
      )}
    </>
  );
};

// check if all array values are {first} or {second} or not affecting risk ROM/SOI
// checkForExistence = true: first or second value should be in the array
const checkArrayForFlagValuesExceptSOIROM = (
  flags: string[],
  first: string,
  second: string,
  checkForExistence = true,
): boolean => {
  const hasTargetFlag = flags.some((flag) => flag === first || flag === second);
  const hasOtherFlags = flags.some(
    (flag) => flag !== first && flag !== second && (!isSOIROMFlag(flag) || affectsRisk(flag)),
  );

  return checkForExistence ? hasTargetFlag : !hasOtherFlags;
};

const getTooltipContentGFCell = (dataItem: any, isFirstCodeRow: boolean) => {
  const { grouperFlag, grouperFlagAffectsDRG, gridCodeType } = dataItem;
  if (!grouperFlag) return <div />;

  const flags = grouperFlag.split(", ");
  const hasADFlag = flags.includes("AD");
  const spans: JSX.Element[] = [];

  flags.forEach((flag: string, index: number) => {
    if (isSOIROMFlag(flag)) {
      spans.push(
        <span key={index}>
          {spans.length ?
            <br />
          : null}
          {getSOIROMTooltip(flag)}
        </span>,
      );
    } else {
      const tooltip = getUsualFlagTooltip(flag);

      if (tooltip) {
        spans.push(
          <span key={index}>
            {spans.length ?
              <br />
            : null}
            {tooltip}
          </span>,
        );
      }
    }
  });

  const addAffectsDRGMessage = (message: string) => {
    spans.push(
      <span key="AD">
        {spans.length ?
          <br />
        : null}
        {message}
      </span>,
    );
  };

  // There is no need to handle it such a way if the message for the AD flag has already been added as a usual tooltip.
  if (!hasADFlag) {
    // Handle AD flag. This logic is different for Diagnoses and InProcedures grids
    if (gridCodeType === GridCodeType.DIAGNOSES) {
      // For Diagnoses:
      // - Show "AD: Affects DRG" if grouperFlagAffectsDRG is true and not first row
      // - Show "Does not affect DRG" if not first row, not affecting DRG, and has CC/MC flags
      if (grouperFlagAffectsDRG && !isFirstCodeRow) {
        addAffectsDRGMessage("AD: Affects DRG");
      } else if (
        !isFirstCodeRow &&
        !grouperFlagAffectsDRG &&
        checkArrayForFlagValuesExceptSOIROM(flags, "CC", "MC")
      ) {
        addAffectsDRGMessage("Does not affect DRG");
      }
    } else if (gridCodeType === GridCodeType.INPROCEDURES) {
      // For InProcedures:
      // - Show "AD: Affects DRG" if grouperFlagAffectsDRG is true
      // - Show "Does not affect DRG" if not affecting DRG and has OR/NOR flags
      if (grouperFlagAffectsDRG) {
        addAffectsDRGMessage("AD: Affects DRG");
      } else if (
        !grouperFlagAffectsDRG &&
        checkArrayForFlagValuesExceptSOIROM(flags, "OR", "NOR")
      ) {
        addAffectsDRGMessage("Does not affect DRG");
      }
    }
  }

  return <div>{spans}</div>;
};
